// apps/admin/src/pages/MobileAppPortal.js
// Native-Feel Mobile Application for Ownezzy
// Merges 100+ Ready-to-Use Demo Stores + Ownezzy Admin Panel
// Features: Native Bottom Navigation Tabs, Virtualized Feed (60 FPS), Bottom Sheet Store Launcher, In-App Previewer
// Pricing: ₹399/month (7-Day Free Trial, 0% Commission)

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, setUser, logout } from "../redux/Slice/authSlice";
import { CATEGORY_FILTERS, DEMO_STORES, PRICING_CONFIG } from "../data/demoStoresData";
import { login as apiLogin, sendOnboardingOtp, verifyOnboardingOtp } from "../services/auth";
import { client } from "../services";
import { getSites, getSiteSettings, updateSiteSettings } from "../services/sites";
import { getOrders, updateOrder } from "../services/orders";
import { getPendingOrders } from "../services/admin";
import { money, dateText } from "../utils/format";
import AdminPanel from "./AdminPanel";
import "./MobileAppPortal.css";

const PAGE_SIZE = 12;

export default function MobileAppPortal({ notify: parentNotify, defaultTab = "stores" }) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // Active Bottom Tab: 'stores' | 'search' | 'admin' | 'account'
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Search, Category & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("popular"); // 'popular' | 'name-asc' | 'name-desc' | 'v2' | 'intl'

  // Paginated Feed state to ensure ultra-smooth 60 FPS scrolling
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Modals & Bottom Sheets
  const [previewStore, setPreviewStore] = useState(null);
  const [launchingStore, setLaunchingStore] = useState(null);
  const [authSheetOpen, setAuthSheetOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // 'login' | 'register'

  // Toast Notification
  const [toast, setToast] = useState(null);
  const notify = useCallback(
    (msg, type = "info") => {
      setToast({ msg, type });
      if (parentNotify) parentNotify(msg, type);
      setTimeout(() => setToast(null), 3500);
    },
    [parentNotify]
  );

  // Reset pagination when category, search, or sort changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, selectedCategory, sortBy]);

  // Horizontal Category Scroll ref
  const categoryScrollRef = useRef(null);

  // Filtered & Sorted Stores (100+ stores catalog)
  const filteredStores = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const catConfig = CATEGORY_FILTERS.find((c) => c.id === selectedCategory);

    let list = DEMO_STORES.filter((store) => {
      // Category match
      let matchesCat = true;
      if (selectedCategory !== "all") {
        if (selectedCategory === "intl") {
          matchesCat = Boolean(store.isIntl);
        } else if (catConfig && catConfig.matches) {
          matchesCat = catConfig.matches.includes(store.baseId || store.template || store.id);
        }
      }
      if (!matchesCat) return false;

      // Search match
      if (!q) return true;
      return (
        store.name.toLowerCase().includes(q) ||
        store.label.toLowerCase().includes(q) ||
        (store.note && store.note.toLowerCase().includes(q)) ||
        (store.pitch && store.pitch.toLowerCase().includes(q)) ||
        (store.business && store.business.toLowerCase().includes(q)) ||
        (store.country && store.country.toLowerCase().includes(q))
      );
    });

    // Sorting
    if (sortBy === "name-asc") {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name-desc") {
      list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === "v2") {
      list = [...list].sort((a, b) => (b.isV2 ? 1 : 0) - (a.isV2 ? 1 : 0));
    } else if (sortBy === "intl") {
      list = [...list].sort((a, b) => (b.isIntl ? 1 : 0) - (a.isIntl ? 1 : 0));
    }

    return list;
  }, [searchQuery, selectedCategory, sortBy]);

  // Paginated slice for buttery smooth mobile rendering
  const displayedStores = useMemo(() => {
    return filteredStores.slice(0, visibleCount);
  }, [filteredStores, visibleCount]);

  const hasMore = visibleCount < filteredStores.length;

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredStores.length));
  };

  return (
    <div className="native-app-root">
      {/* Toast Notification */}
      {toast && (
        <div className={`native-toast ${toast.type}`}>
          <span className="toast-icon">{toast.type === "error" ? "⚠️" : "✓"}</span>
          <span className="toast-text">{toast.msg}</span>
        </div>
      )}

      {/* Native App Top Header */}
      <header className="native-top-appbar">
        <div className="native-appbar-brand" onClick={() => setActiveTab("stores")}>
          <span className="native-brand-logo">
            <span className="brand-owne">owne</span>
            <span className="brand-zzy">zzy</span>
          </span>
          <span className="native-brand-pill">APP</span>
        </div>

        <div className="native-appbar-actions">
          {user ? (
            <button
              type="button"
              className="native-user-chip"
              onClick={() => setActiveTab("account")}
              title={`Account: ${user.name}`}
            >
              <span className="chip-avatar">{user.name.charAt(0).toUpperCase()}</span>
              <span className="chip-name">{user.name.split(" ")[0]}</span>
            </button>
          ) : (
            <button
              type="button"
              className="native-header-signin-btn"
              onClick={() => {
                setAuthMode("login");
                setAuthSheetOpen(true);
              }}
            >
              Sign In
            </button>
          )}
        </div>
      </header>

      {/* App Body - Routed by active bottom tab */}
      <main className="native-app-body">
        {/* TAB 1: STORES DISCOVERY FEED */}
        {activeTab === "stores" && (
          <div className="native-tab-view stores-feed-view">
            {/* Native Mobile Hero Banner */}
            <div className="native-hero-card">
              <div className="hero-eyebrow">
                <span className="eyebrow-dot" />
                <span>100+ STORE WEBSITES • ₹399/MONTH</span>
              </div>
              <h1 className="hero-heading">Launch Your Online Store in 60s</h1>
              <p className="hero-subtext">
                Pick a ready-made website for your business. 7 days free, then just ₹399/mo. 0% commission, direct UPI &amp; WhatsApp alerts.
              </p>

              <div className="hero-perks-row">
                <span className="hero-perk">⚡ Instant 60s Live</span>
                <span className="hero-perk">💰 0% Commission</span>
                <span className="hero-perk">🎁 7 Days Free</span>
              </div>
            </div>

            {/* Quick Search Field in Explore */}
            <div className="native-search-container">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="native-search-input"
                placeholder="Search 100+ stores: Kurtis, Plants, Cakes, Gifts…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchQuery("")}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Horizontal Categories Scroll */}
            <div className="native-category-scroller" ref={categoryScrollRef}>
              {CATEGORY_FILTERS.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`native-cat-chip ${selectedCategory === cat.id ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span className="cat-chip-icon">{cat.icon}</span>
                  <span className="cat-chip-label">{cat.label}</span>
                </button>
              ))}
            </div>

            {/* Feed Results Bar */}
            <div className="native-feed-toolbar">
              <span className="feed-count">
                Showing <b>{filteredStores.length}</b> stores
                {selectedCategory !== "all" && ` in ${CATEGORY_FILTERS.find((c) => c.id === selectedCategory)?.label}`}
              </span>

              <select
                className="native-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="popular">Recommended</option>
                <option value="name-asc">A → Z</option>
                <option value="name-desc">Z → A</option>
                <option value="v2">Studio v2</option>
                <option value="intl">International</option>
              </select>
            </div>

            {/* Stores List (Mobile-Optimized Cards) */}
            <div className="native-stores-list">
              {displayedStores.map((store) => (
                <article key={store.id} className="native-store-card">
                  {/* Browser Snapshot Frame */}
                  <div
                    className="card-preview-frame"
                    onClick={() => setPreviewStore(store)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setPreviewStore(store)}
                  >
                    <div className="card-browser-mockup">
                      <span className="browser-dots">
                        <i />
                        <i />
                        <i />
                      </span>
                      <span className="browser-slug">{store.demo}.ownezzy.com</span>
                    </div>

                    <div className="card-image-holder">
                      <img
                        src={store.preview}
                        alt={`${store.name} - ${store.label}`}
                        className="card-preview-image"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div
                        className="card-image-fallback"
                        style={{
                          background: `linear-gradient(135deg, ${store.theme?.main || "#10b981"}, #0f172a)`,
                        }}
                      >
                        <span style={{ fontSize: "36px" }}>{store.icon}</span>
                        <span>{store.name}</span>
                      </div>
                    </div>

                    {/* Overlay Badges */}
                    <div className="card-overlay-badges">
                      <span className="badge-live">
                        <span className="live-pulse-dot" /> Live Demo
                      </span>
                      {store.isV2 && <span className="badge-v2">Studio v2</span>}
                      {store.isIntl && <span className="badge-intl">🌍 {store.country}</span>}
                    </div>

                    <span className="badge-category">
                      <span>{store.icon}</span>
                      <span>{store.label}</span>
                    </span>
                  </div>

                  {/* Card Content Details */}
                  <div className="card-content-body">
                    <div className="card-title-row">
                      <h2 className="card-store-name">{store.name}</h2>
                      <span className="card-store-niche">{store.label}</span>
                    </div>

                    <p className="card-store-pitch">{store.pitch || store.note}</p>

                    {/* Pricing Row */}
                    <div className="card-pricing-row">
                      <div className="pricing-left">
                        <span className="price-tag">₹399</span>
                        <span className="price-unit">/ month</span>
                      </div>
                      <div className="pricing-badges">
                        <span className="trial-pill">7-Day Free Trial</span>
                        <span className="zero-comm-pill">0% Commission</span>
                      </div>
                    </div>

                    {/* Touch Action Buttons */}
                    <div className="card-action-buttons">
                      <button
                        type="button"
                        className="btn-card-preview"
                        onClick={() => setPreviewStore(store)}
                      >
                        <span>👁️ Live Demo</span>
                      </button>
                      <button
                        type="button"
                        className="btn-card-launch"
                        onClick={() => setLaunchingStore(store)}
                      >
                        <span>⚡ Get This Store</span>
                      </button>
                    </div>

                    <div className="card-foot-notice">
                      7 days free • ₹399/mo after • Cancel anytime
                    </div>
                  </div>
                </article>
              ))}

              {filteredStores.length === 0 && (
                <div className="native-empty-state">
                  <span className="empty-icon">🔍</span>
                  <h3>No matching websites found</h3>
                  <p>Try searching for clothing, dry fruits, bakery, jewellery, or gifts.</p>
                  <button
                    type="button"
                    className="btn-reset-filters"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("all");
                    }}
                  >
                    View All 100+ Websites
                  </button>
                </div>
              )}
            </div>

            {/* Load More Pagination Button */}
            {hasMore && (
              <div className="native-load-more-wrap">
                <button type="button" className="btn-native-load-more" onClick={loadMore}>
                  <span>Load More Stores</span>
                  <span className="load-more-counter">
                    ({displayedStores.length} of {filteredStores.length})
                  </span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: SEARCH & DISCOVERY BY NICHE */}
        {activeTab === "search" && (
          <div className="native-tab-view search-view">
            <div className="native-section-header">
              <h2>Browse by Business Category</h2>
              <p>Explore 100+ specialized ready-to-use e-commerce store templates</p>
            </div>

            <div className="native-search-container" style={{ margin: "0 0 16px 0" }}>
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="native-search-input"
                placeholder="Search all businesses & products…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchQuery("")}
                >
                  ✕
                </button>
              )}
            </div>

            <div className="native-category-grid">
              {CATEGORY_FILTERS.map((cat) => {
                const count =
                  cat.id === "all"
                    ? DEMO_STORES.length
                    : cat.id === "intl"
                    ? DEMO_STORES.filter((s) => s.isIntl).length
                    : DEMO_STORES.filter((s) => (cat.matches || []).includes(s.baseId || s.template || s.id)).length;

                return (
                  <button
                    key={cat.id}
                    type="button"
                    className={`native-cat-card ${selectedCategory === cat.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      setActiveTab("stores");
                    }}
                  >
                    <span className="cat-card-icon">{cat.icon}</span>
                    <div className="cat-card-info">
                      <span className="cat-card-name">{cat.label}</span>
                      <span className="cat-card-count">{count} stores</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* TAB 3: MY STORE ADMIN */}
        {activeTab === "admin" && (
          <div className="native-tab-view admin-view">
            {user ? (
              <NativeStoreManager
                user={user}
                notify={notify}
                onBrowseStores={() => setActiveTab("stores")}
              />
            ) : (
              <div className="native-signin-prompt-card">
                <span className="signin-lock-icon">🔐</span>
                <h2>Sign In to Manage Your Store</h2>
                <p>
                  Access your orders, product catalog, customer delivery addresses, UPI payment setup, and store settings.
                </p>
                <button
                  type="button"
                  className="btn-native-primary"
                  onClick={() => {
                    setAuthMode("login");
                    setAuthSheetOpen(true);
                  }}
                >
                  Sign In to Admin Panel
                </button>
                <div style={{ marginTop: "14px" }}>
                  <button
                    type="button"
                    className="btn-native-secondary"
                    onClick={() => setActiveTab("stores")}
                  >
                    Don&apos;t have a store? Browse 100+ Stores
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: ACCOUNT, APK & SUPPORT */}
        {activeTab === "account" && (
          <div className="native-tab-view account-view">
            <div className="native-account-profile-card">
              {user ? (
                <>
                  <div className="account-avatar-large">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <h2 className="account-user-name">{user.name}</h2>
                  <span className="account-user-email">{user.email}</span>
                  <span className="account-user-role">Role: {user.role}</span>
                </>
              ) : (
                <>
                  <div className="account-avatar-large">👤</div>
                  <h2 className="account-user-name">Welcome to Ownezzy</h2>
                  <p className="account-user-email">Launch or manage your online store</p>
                  <button
                    type="button"
                    className="btn-native-primary"
                    style={{ margin: "12px auto 0", maxWidth: "200px" }}
                    onClick={() => {
                      setAuthMode("login");
                      setAuthSheetOpen(true);
                    }}
                  >
                    Sign In
                  </button>
                </>
              )}
            </div>

            {/* Transparent Pricing Card */}
            <div className="native-pricing-info-card">
              <div className="pic-header">
                <h3>💎 Transparent Ownezzy Pricing</h3>
                <span className="pic-badge">Official Plan</span>
              </div>
              <div className="pic-price-row">
                <span className="pic-price">₹399</span>
                <span className="pic-period">/ month per store</span>
              </div>
              <p className="pic-trial-note">Includes 7-day 100% Free Trial. Cancel anytime.</p>

              <div className="pic-perks-list">
                <div className="pic-perk">✓ 100+ business templates ready to launch in 60s</div>
                <div className="pic-perk">✓ Free subdomain (yourshop.ownezzy.com) &amp; SSL</div>
                <div className="pic-perk">✓ 0% commission on direct UPI (GPay/PhonePe/Paytm)</div>
                <div className="pic-perk">✓ Automated WhatsApp customer order alerts</div>
                <div className="pic-perk">✓ Full mobile admin panel &amp; Android APK access</div>
              </div>
            </div>

            {/* Mobile App Download & Share */}
            <div className="native-app-distribution-card">
              <h3>📱 Ownezzy Mobile App</h3>
              <p>Keep the official APK on your phone or share with fellow business owners.</p>
              <div className="app-dist-buttons">
                <a
                  href="/downloads/ownezzy.apk"
                  download="ownezzy.apk"
                  className="btn-dist-download"
                >
                  <span>⬇ Download Android APK (6.8 MB)</span>
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    "Launch your online store in 60 seconds with Ownezzy: 100+ ready store designs, ₹399/mo with 7-day free trial, 0% commission! Download app: https://admin.ownezzy.com/downloads/ownezzy.apk"
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dist-share"
                >
                  <span>💬 Share on WhatsApp</span>
                </a>
              </div>
            </div>

            {/* Help & Logout */}
            <div className="native-account-actions-card">
              <a
                href="https://wa.me/918893725078?text=Hi%20Ownezzy%20Support,%20I%20need%20help%20with%20my%20mobile%20app"
                target="_blank"
                rel="noopener noreferrer"
                className="native-action-row"
              >
                <span>💬 WhatsApp Support</span>
                <span>›</span>
              </a>
              <a href="https://ownezzy.com/guide" target="_blank" rel="noopener noreferrer" className="native-action-row">
                <span>📖 User Guide &amp; Tutorials</span>
                <span>›</span>
              </a>
              {user && (
                <button
                  type="button"
                  className="native-action-row logout-row"
                  onClick={() => {
                    dispatch(logout());
                    notify("Signed out successfully", "info");
                    setActiveTab("stores");
                  }}
                >
                  <span>🚪 Sign Out</span>
                  <span>›</span>
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* NATIVE BOTTOM NAVIGATION BAR */}
      <nav className="native-bottom-nav" aria-label="Main Navigation">
        <button
          type="button"
          className={`nav-tab-item ${activeTab === "stores" ? "active" : ""}`}
          onClick={() => setActiveTab("stores")}
        >
          <span className="nav-tab-icon">🏪</span>
          <span className="nav-tab-label">Stores</span>
          <span className="nav-tab-badge">100+</span>
        </button>

        <button
          type="button"
          className={`nav-tab-item ${activeTab === "search" ? "active" : ""}`}
          onClick={() => setActiveTab("search")}
        >
          <span className="nav-tab-icon">🔍</span>
          <span className="nav-tab-label">Search</span>
        </button>

        <button
          type="button"
          className={`nav-tab-item ${activeTab === "admin" ? "active" : ""}`}
          onClick={() => {
            if (user) {
              setActiveTab("admin");
            } else {
              setAuthMode("login");
              setAuthSheetOpen(true);
            }
          }}
        >
          <span className="nav-tab-icon">📊</span>
          <span className="nav-tab-label">My Store</span>
        </button>

        <button
          type="button"
          className={`nav-tab-item ${activeTab === "account" ? "active" : ""}`}
          onClick={() => setActiveTab("account")}
        >
          <span className="nav-tab-icon">👤</span>
          <span className="nav-tab-label">Account</span>
        </button>
      </nav>

      {/* IN-APP STORE LIVE PREVIEWER SHEET */}
      {previewStore && (
        <div className="native-preview-overlay">
          <div className="native-preview-sheet">
            <div className="preview-top-bar">
              <div className="preview-header-info">
                <span className="preview-icon">{previewStore.icon}</span>
                <div>
                  <h3 className="preview-store-name">{previewStore.name}</h3>
                  <span className="preview-store-url">{previewStore.demo}.ownezzy.com</span>
                </div>
              </div>
              <div className="preview-header-actions">
                <a
                  href={previewStore.url || `https://${previewStore.demo}.ownezzy.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-preview-external"
                >
                  ↗ Browser
                </a>
                <button
                  type="button"
                  className="btn-preview-close"
                  onClick={() => setPreviewStore(null)}
                  aria-label="Close Preview"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="preview-iframe-wrapper">
              <iframe
                src={previewStore.url || `https://${previewStore.demo}.ownezzy.com`}
                title={`Live Preview of ${previewStore.name}`}
                className="preview-iframe"
              />
            </div>

            <div className="preview-bottom-cta">
              <div className="preview-price-info">
                <b>₹399 / month</b>
                <span>7-Day Free Trial • 0% Fee</span>
              </div>
              <button
                type="button"
                className="btn-preview-launch"
                onClick={() => {
                  const s = previewStore;
                  setPreviewStore(null);
                  setLaunchingStore(s);
                }}
              >
                ⚡ Get This Website
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NATIVE BOTTOM SHEET: QUICK STORE LAUNCHER ("GET STORE") */}
      {launchingStore && (
        <NativeStoreLauncherSheet
          store={launchingStore}
          user={user}
          notify={notify}
          onClose={() => setLaunchingStore(null)}
          onStoreCreated={() => {}}
          onGoToAdmin={() => {
            setLaunchingStore(null);
            setActiveTab("admin");
          }}
        />
      )}

      {/* NATIVE BOTTOM SHEET: AUTH (LOGIN & REGISTER) */}
      {authSheetOpen && (
        <NativeAuthSheet
          mode={authMode}
          onSwitchMode={(m) => setAuthMode(m)}
          onClose={() => setAuthSheetOpen(false)}
          notify={notify}
          onSuccess={(loggedInUser) => {
            setAuthSheetOpen(false);
            notify(`Welcome back, ${loggedInUser.name}!`, "success");
            setActiveTab("admin");
          }}
        />
      )}
    </div>
  );
}

// Subcomponent: Native Bottom Sheet for Store Launch & Instant Buy
function NativeStoreLauncherSheet({ store, user, notify, onClose, onStoreCreated, onGoToAdmin }) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(user ? "shopDetails" : "credentials"); // 'credentials' | 'shopDetails' | 'success'

  // Form Fields
  const [shopName, setShopName] = useState(store ? `${store.name} Store` : "");
  const [subdomain, setSubdomain] = useState(
    store ? store.demo.toLowerCase().replace(/[^a-z0-9]/g, "") : ""
  );
  const [plan, setPlan] = useState("monthly"); // 'trial' | 'monthly' | 'yearly'

  // New User Fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");

  const [loading, setLoading] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);

  const handleShopNameChange = (val) => {
    setShopName(val);
    const slug = val.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-");
    setSubdomain(slug);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email || !phone) {
      notify("Please enter your email and WhatsApp number", "error");
      return;
    }
    try {
      setLoading(true);
      const res = await sendOnboardingOtp(phone, email);
      setOtpSent(true);
      notify("Verification code sent to your email! (Test code: " + (res.data?.devOtp || "123456") + ")", "info");
      if (res.data?.devOtp) setOtp(res.data.devOtp);
    } catch (err) {
      notify(err.response?.data?.message || "Failed to send verification code", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      notify("Please enter the 6-digit verification code", "error");
      return;
    }
    try {
      setLoading(true);
      const res = await verifyOnboardingOtp(phone, email, otp);
      setVerificationToken(res.data.verificationToken);
      notify("Email verified successfully!", "success");
      setStep("shopDetails");
    } catch (err) {
      notify(err.response?.data?.message || "Invalid verification code", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStore = async (e) => {
    e.preventDefault();
    if (!shopName.trim()) {
      notify("Please enter a shop name", "error");
      return;
    }
    try {
      setLoading(true);
      const templateKey = store.template || store.baseId || store.id;

      let res;
      if (user) {
        res = await client.post("/onboarding/user-store", {
          shopName,
          subdomain,
          template: templateKey,
          plan,
        });
      } else {
        res = await client.post("/onboarding/stores", {
          verificationToken,
          shopName,
          template: templateKey,
          plan,
          adminPassword: password || undefined,
        });
        if (res.data?.details?.adminId) {
          try {
            const loginRes = await apiLogin({
              login: email,
              password: password || res.data.details.adminPassword,
            });
            dispatch(setUser(loginRes.data.user));
          } catch {
            // continue
          }
        }
      }

      const storeData = res.data?.store || {
        name: shopName,
        slug: subdomain,
        url: `https://${subdomain}.ownezzy.com`,
      };

      setCreatedResult({
        ...storeData,
        details: res.data?.details,
      });
      setStep("success");
      notify("🎉 Your website has been created successfully!", "success");
      onStoreCreated?.(storeData);
    } catch (err) {
      notify(err.response?.data?.message || "Could not create store. Try another name.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="native-bottom-sheet-overlay" onClick={onClose}>
      <div className="native-bottom-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-drag-handle" />

        {/* Selected Store Summary Card */}
        <div className="sheet-store-header">
          <span className="sheet-store-icon">{store.icon}</span>
          <div className="sheet-store-info">
            <h3 className="sheet-store-title">{store.name}</h3>
            <span className="sheet-store-type">{store.label}</span>
          </div>
          <button type="button" className="sheet-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Transparent Pricing Highlight */}
        <div className="sheet-pricing-banner">
          <div className="spb-left">
            <span className="spb-label">Subscription:</span>
            <b className="spb-rate">₹399 / month</b>
          </div>
          <span className="spb-trial-pill">7-Day Free Trial (₹0 today)</span>
        </div>

        {/* STEP 1: Registration / Verification for Guests */}
        {step === "credentials" && (
          <form className="sheet-form-body" onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
            <p className="sheet-instruction">
              Enter your WhatsApp number &amp; email to create your store. We will email you a 6-digit code.
            </p>

            <div className="sheet-input-group">
              <label>Full Name</label>
              <input
                type="text"
                placeholder="e.g. Anand Sharma"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={otpSent}
              />
            </div>

            <div className="sheet-input-group">
              <label>WhatsApp Number</label>
              <input
                type="tel"
                placeholder="e.g. 9876543210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                disabled={otpSent}
              />
            </div>

            <div className="sheet-input-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={otpSent}
              />
            </div>

            {otpSent && (
              <>
                <div className="sheet-input-group">
                  <label>6-Digit Verification Code</label>
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.trim())}
                    maxLength={6}
                    required
                  />
                </div>
                <div className="sheet-input-group">
                  <label>Password (Optional)</label>
                  <input
                    type="password"
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            )}

            <button type="submit" className="btn-sheet-submit" disabled={loading}>
              {loading ? "Please wait…" : otpSent ? "Verify Code & Proceed" : "Send Verification Code"}
            </button>
          </form>
        )}

        {/* STEP 2: Shop Details & Launch */}
        {step === "shopDetails" && (
          <form className="sheet-form-body" onSubmit={handleCreateStore}>
            {user && (
              <div className="sheet-user-pill">
                <span>✓</span>
                <span>
                  Logged in as <b>{user.name}</b> ({user.email}). Zero OTP needed!
                </span>
              </div>
            )}

            <div className="sheet-input-group">
              <label>Your Shop / Brand Name</label>
              <input
                type="text"
                placeholder="e.g. Maya Fashion Boutique"
                value={shopName}
                onChange={(e) => handleShopNameChange(e.target.value)}
                required
              />
            </div>

            <div className="sheet-input-group">
              <label>Website Subdomain</label>
              <input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                required
              />
              <span className="sheet-subdomain-hint">
                🌐 Live Link: https://{subdomain || "yourshop"}.ownezzy.com
              </span>
            </div>

            <div className="sheet-input-group">
              <label>Choose Plan</label>
              <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                <option value="monthly">Monthly Plan — ₹399/mo (7-Day Free Trial, then ₹399/mo)</option>
                <option value="yearly">Yearly Plan — ₹4,499/yr (Save 6% — 7-Day Free Trial)</option>
                <option value="trial">7-Day Free Trial (₹0 today — No credit card required)</option>
              </select>
            </div>

            <div className="sheet-features-checklist">
              <div className="sfc-item">✓ Complete store ready with demo products</div>
              <div className="sfc-item">✓ Direct UPI payments with 0% commission</div>
              <div className="sfc-item">✓ Automated WhatsApp order confirmations</div>
              <div className="sfc-item">✓ Free SSL certificate &amp; custom domain support</div>
            </div>

            <button type="submit" className="btn-sheet-submit" disabled={loading}>
              {loading ? "Creating Your Website…" : "⚡ Launch My Store Now"}
            </button>
          </form>
        )}

        {/* STEP 3: Success & Download APK */}
        {step === "success" && (
          <div className="sheet-success-body">
            <span className="success-emoji">🎉</span>
            <h3>Your Store is Live!</h3>
            <p>Your online store has been generated and is live on the internet.</p>

            <div className="success-link-card">
              <span className="slc-label">Your Live Storefront:</span>
              <a
                href={createdResult?.url || `https://${subdomain}.ownezzy.com`}
                target="_blank"
                rel="noopener noreferrer"
                className="slc-url"
              >
                {createdResult?.url || `https://${subdomain}.ownezzy.com`}
              </a>
            </div>

            <div className="success-buttons-group">
              <button type="button" className="btn-success-admin" onClick={onGoToAdmin}>
                <span>📊 Open in My Admin Panel</span>
              </button>
              <a
                href="/downloads/ownezzy.apk"
                download="ownezzy.apk"
                className="btn-success-apk"
              >
                <span>📱 Download Ownezzy Mobile APK</span>
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `Check out our new online store: https://${subdomain}.ownezzy.com\n\nDownload our mobile app: https://admin.ownezzy.com/downloads/ownezzy.apk`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-success-share"
              >
                <span>💬 Share on WhatsApp</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Subcomponent: Native Bottom Sheet for Login
function NativeAuthSheet({ mode = "login", onSwitchMode, onClose, notify, onSuccess }) {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(mode);
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await apiLogin({ login: loginEmail, password: loginPassword });
      dispatch(setUser(res.data.user));
      onSuccess?.(res.data.user);
    } catch (err) {
      notify(err.response?.data?.message || "Invalid email or password", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="native-bottom-sheet-overlay" onClick={onClose}>
      <div className="native-bottom-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-drag-handle" />

        <div className="sheet-auth-header">
          <div className="sheet-auth-tabs">
            <button
              type="button"
              className={`sheet-auth-tab ${activeTab === "login" ? "active" : ""}`}
              onClick={() => setActiveTab("login")}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`sheet-auth-tab ${activeTab === "register" ? "active" : ""}`}
              onClick={() => setActiveTab("register")}
            >
              Register
            </button>
          </div>
          <button type="button" className="sheet-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {activeTab === "login" ? (
          <form className="sheet-form-body" onSubmit={handleLogin}>
            <div className="sheet-input-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
              />
            </div>
            <div className="sheet-input-group">
              <label>Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn-sheet-submit" disabled={loading}>
              {loading ? "Signing In…" : "Sign In to Admin Panel"}
            </button>
          </form>
        ) : (
          <div className="sheet-form-body" style={{ textAlign: "center", padding: "24px 16px" }}>
            <span style={{ fontSize: "40px" }}>🏪</span>
            <h3 style={{ margin: "10px 0 6px" }}>Get a Demo Store to Register</h3>
            <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 16px" }}>
              To create your account, choose any of our 100+ business templates and tap &quot;Get This Store&quot;. Your account is created in 60s!
            </p>
            <button type="button" className="btn-sheet-submit" onClick={onClose}>
              Browse 100+ Stores
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Subcomponent: Native-Feel Mobile Store Manager (Replaces desktop redirect with 100% adaptive mobile UI)
function NativeStoreManager({ user, notify, onBrowseStores }) {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [loadingSites, setLoadingSites] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

  // Subviews: null (Dashboard) | 'orders' | 'settings' | 'fullAdmin'
  const [subView, setSubView] = useState(null);

  // Orders Filter
  const [orderFilter, setOrderFilter] = useState("all");
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  // Quick Store Settings
  const [storeSettings, setStoreSettings] = useState({
    name: "",
    storeName: "",
    contact: "",
    orderWhatsapp: "",
    upiId: "",
    upiName: "",
    shipping: "0",
    freeShippingAbove: "0",
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Load Sites & Pending Orders
  const loadSites = useCallback(async () => {
    try {
      setLoadingSites(true);
      const res = await getSites({ limit: 50, sort: "oldest" });
      const fetched = res.data?.sites || [];
      setSites(fetched);
      if (fetched.length > 0) {
        setSelectedSiteId((prev) => (fetched.some((s) => s.id === prev) ? prev : fetched[0].id));
      }
    } catch (err) {
      notify(err.response?.data?.message || "Failed to load stores", "error");
    } finally {
      setLoadingSites(false);
    }
  }, [notify]);

  useEffect(() => {
    loadSites();
    getPendingOrders()
      .then((res) => setPendingOrdersCount(res.data?.pending || 0))
      .catch(() => {});
  }, [loadSites]);

  const selectedSite = useMemo(() => {
    return sites.find((s) => s.id === selectedSiteId) || sites[0] || null;
  }, [sites, selectedSiteId]);

  // Load Orders & Settings for selected site
  const loadOrders = useCallback(async () => {
    if (!selectedSite?.id) return;
    try {
      setLoadingOrders(true);
      const res = await getOrders(selectedSite.id, { limit: 50 });
      setOrders(res.data?.orders || []);
    } catch {
      // ignore
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite?.id) {
      loadOrders();
      getSiteSettings(selectedSite.id)
        .then((res) => {
          if (res.data?.settings) {
            setStoreSettings((prev) => ({
              ...prev,
              ...res.data.settings,
              name: res.data.settings.name || "",
              storeName: res.data.settings.storeName || selectedSite.name || "",
              contact: res.data.settings.contact || "",
              orderWhatsapp: res.data.settings.orderWhatsapp || "",
              upiId: res.data.settings.upiId || "",
              upiName: res.data.settings.upiName || "",
              shipping: String(res.data.settings.shipping ?? "0"),
              freeShippingAbove: String(res.data.settings.freeShippingAbove ?? "0"),
            }));
          }
        })
        .catch(() => {});
    }
  }, [selectedSite, loadOrders]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!selectedSite?.id) return;
    try {
      setUpdatingOrderId(orderId);
      await updateOrder(selectedSite.id, orderId, { status: newStatus });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
      notify(`Order #${orderId} marked as ${newStatus}`, "success");
    } catch (err) {
      notify(err.response?.data?.message || "Failed to update order", "error");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleMarkPaid = async (orderId) => {
    if (!selectedSite?.id) return;
    try {
      setUpdatingOrderId(orderId);
      await updateOrder(selectedSite.id, orderId, { paymentStatus: "paid" });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, paymentStatus: "paid" } : o))
      );
      notify(`Order #${orderId} marked as Paid`, "success");
    } catch (err) {
      notify(err.response?.data?.message || "Failed to mark paid", "error");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!selectedSite?.id) return;
    try {
      setSavingSettings(true);
      await updateSiteSettings(selectedSite.id, storeSettings);
      notify("Store settings saved successfully!", "success");
      setSubView(null);
    } catch (err) {
      notify(err.response?.data?.message || "Failed to save settings", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const getWhatsappUrl = (order) => {
    const raw = String(order.customer?.phone || "").replace(/\D/g, "");
    const phone = raw.length === 10 ? `91${raw}` : raw;
    if (!phone) return null;
    const storeName = selectedSite?.name || "our store";
    const msg = `Hello ${order.customer?.name || "Customer"}, this is ${storeName}. I am writing about your order #${order.id} for ${money(order.total)}.`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  const totalRevenue = useMemo(() => {
    return orders
      .filter((o) => o.status !== "Cancelled" && o.status !== "Refunded")
      .reduce((sum, o) => sum + (Number(o.total) || 0), 0);
  }, [orders]);

  const activePendingOrders = useMemo(() => {
    return orders.filter((o) => (o.status || "").toLowerCase() === "pending");
  }, [orders]);

  const completedOrdersCount = useMemo(() => {
    return orders.filter((o) => (o.status || "").toLowerCase() === "delivered").length;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (orderFilter === "all") return orders;
    return orders.filter(
      (o) => (o.status || "").toLowerCase() === orderFilter.toLowerCase()
    );
  }, [orders, orderFilter]);

  // VIEW 1: Embedded Full Workspace
  if (subView === "fullAdmin") {
    return (
      <div className="native-subview-wrapper">
        <div className="native-subview-appbar">
          <button
            type="button"
            className="btn-subview-back"
            onClick={() => setSubView(null)}
          >
            ‹ Back to Dashboard
          </button>
          <span className="subview-header-title">Full Workspace</span>
        </div>
        <AdminPanel embedded={true} onBack={() => setSubView(null)} />
      </div>
    );
  }

  // VIEW 2: Native Mobile Orders Manager
  if (subView === "orders") {
    return (
      <div className="native-subview-wrapper">
        <div className="native-subview-appbar">
          <button
            type="button"
            className="btn-subview-back"
            onClick={() => setSubView(null)}
          >
            ‹ Back
          </button>
          <span className="subview-header-title">
            Orders ({orders.length})
          </span>
          <button
            type="button"
            className="btn-subview-refresh"
            onClick={loadOrders}
            title="Refresh Orders"
          >
            ↻
          </button>
        </div>

        <div className="native-subview-body">
          {/* Order Filter Chips */}
          <div className="native-orders-filter-scroller">
            {["all", "pending", "confirmed", "shipped", "delivered"].map((f) => (
              <button
                key={f}
                type="button"
                className={`order-filter-chip ${orderFilter === f ? "active" : ""}`}
                onClick={() => setOrderFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === "pending" && activePendingOrders.length > 0 && (
                  <span className="chip-badge">{activePendingOrders.length}</span>
                )}
              </button>
            ))}
          </div>

          {loadingOrders ? (
            <div className="native-loading-card">
              <span className="native-spinner" />
              <p>Loading orders…</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="native-empty-card">
              <span style={{ fontSize: "36px" }}>📦</span>
              <h4>No {orderFilter !== "all" ? orderFilter : ""} orders yet</h4>
              <p>When customers order from your storefront, they will appear here in real time.</p>
            </div>
          ) : (
            <div className="native-orders-card-list">
              {filteredOrders.map((order) => {
                const whatsappUrl = getWhatsappUrl(order);
                const isPending = (order.status || "").toLowerCase() === "pending";
                const isConfirmed = (order.status || "").toLowerCase() === "confirmed";
                const isShipped = (order.status || "").toLowerCase() === "shipped";
                const isDelivered = (order.status || "").toLowerCase() === "delivered";

                return (
                  <div key={order.id} className="native-order-item-card">
                    <div className="order-item-head">
                      <div>
                        <span className="order-id-label">#{order.id}</span>
                        <span className="order-date-label">{dateText(order.createdAt)}</span>
                      </div>
                      <span className={`order-status-badge ${order.status?.toLowerCase()}`}>
                        {order.status || "Pending"}
                      </span>
                    </div>

                    <div className="order-customer-box">
                      <div className="customer-info-line">
                        <b>👤 {order.customer?.name || "Customer"}</b>
                        <span>📞 {order.customer?.phone || "No phone"}</span>
                      </div>
                      {order.customer?.address && (
                        <p className="customer-address-line">
                          📍 {order.customer.address}
                        </p>
                      )}
                    </div>

                    <div className="order-items-summary">
                      <span className="order-items-text">
                        {order.items && order.items.length > 0
                          ? order.items.map((i) => `${i.quantity || 1}x ${i.name}`).join(", ")
                          : "Store items"}
                      </span>
                      <div className="order-amount-row">
                        <span className="order-total-price">{money(order.total)}</span>
                        <span className={`payment-pill ${order.paymentStatus === "paid" ? "paid" : "pending"}`}>
                          {order.payment || "UPI"} • {order.paymentStatus === "paid" ? "Paid" : "Awaiting"}
                        </span>
                      </div>
                    </div>

                    <div className="order-touch-actions">
                      {whatsappUrl && (
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-order-wa"
                        >
                          💬 WhatsApp Buyer
                        </a>
                      )}

                      {isPending && (
                        <button
                          type="button"
                          className="btn-order-confirm"
                          disabled={updatingOrderId === order.id}
                          onClick={() => handleUpdateStatus(order.id, "Confirmed")}
                        >
                          {updatingOrderId === order.id ? "…" : "✓ Confirm"}
                        </button>
                      )}

                      {isConfirmed && (
                        <button
                          type="button"
                          className="btn-order-ship"
                          disabled={updatingOrderId === order.id}
                          onClick={() => handleUpdateStatus(order.id, "Shipped")}
                        >
                          {updatingOrderId === order.id ? "…" : "🚚 Ship"}
                        </button>
                      )}

                      {isShipped && (
                        <button
                          type="button"
                          className="btn-order-deliver"
                          disabled={updatingOrderId === order.id}
                          onClick={() => handleUpdateStatus(order.id, "Delivered")}
                        >
                          {updatingOrderId === order.id ? "…" : "🎉 Delivered"}
                        </button>
                      )}

                      {order.payment === "Manual UPI" && order.paymentStatus === "pending" && (
                        <button
                          type="button"
                          className="btn-order-paid"
                          disabled={updatingOrderId === order.id}
                          onClick={() => handleMarkPaid(order.id)}
                        >
                          💰 Mark Paid
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // VIEW 3: Native Mobile Settings
  if (subView === "settings") {
    return (
      <div className="native-subview-wrapper">
        <div className="native-subview-appbar">
          <button
            type="button"
            className="btn-subview-back"
            onClick={() => setSubView(null)}
          >
            ‹ Back
          </button>
          <span className="subview-header-title">Store Settings</span>
        </div>

        <div className="native-subview-body">
          <form className="native-settings-form" onSubmit={handleSaveSettings}>
            <div className="settings-section-card">
              <h4>🏪 Store Identity</h4>
              <div className="native-field-group">
                <label>Shop / Brand Name</label>
                <input
                  type="text"
                  value={storeSettings.storeName}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, storeName: e.target.value })
                  }
                  required
                />
              </div>

              <div className="native-field-group">
                <label>Contact Phone Number</label>
                <input
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={storeSettings.contact}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, contact: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="settings-section-card">
              <h4>📲 Direct UPI (0% Commission)</h4>
              <p className="section-help-text">
                Receive 100% payments directly into your bank account via GPay, PhonePe, or Paytm with zero platform cut.
              </p>
              <div className="native-field-group">
                <label>Your UPI ID (VPA)</label>
                <input
                  type="text"
                  placeholder="e.g. yourname@okaxis or yourphone@ybl"
                  value={storeSettings.upiId}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, upiId: e.target.value })
                  }
                />
              </div>

              <div className="native-field-group">
                <label>Payee Name (as in UPI App)</label>
                <input
                  type="text"
                  placeholder="e.g. Maya Store"
                  value={storeSettings.upiName}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, upiName: e.target.value })
                  }
                />
              </div>

              <div className="native-field-group">
                <label>WhatsApp Number for Order Alerts</label>
                <input
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={storeSettings.orderWhatsapp}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, orderWhatsapp: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="settings-section-card">
              <h4>🚚 Delivery Fees</h4>
              <div className="native-field-group">
                <label>Standard Delivery Fee (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 50"
                  value={storeSettings.shipping}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, shipping: e.target.value })
                  }
                />
              </div>

              <div className="native-field-group">
                <label>Free Delivery for Orders Above (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 499 (0 for no free shipping)"
                  value={storeSettings.freeShippingAbove}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, freeShippingAbove: e.target.value })
                  }
                />
              </div>
            </div>

            <button type="submit" className="btn-native-primary" disabled={savingSettings}>
              {savingSettings ? "Saving Changes…" : "Save Store Settings"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // VIEW 4: Main Native Mobile Store Dashboard
  return (
    <div className="native-store-dashboard">
      {/* Super Admin Notice */}
      {user.role === "superadmin" && (
        <div className="superadmin-mobile-banner">
          <span>👑 Super Admin: {sites.length} Active Stores</span>
          <button
            type="button"
            className="btn-sa-full"
            onClick={() => setSubView("fullAdmin")}
          >
            Open Full Workspace ›
          </button>
        </div>
      )}

      {/* Multi-Store Switcher */}
      {sites.length > 1 && (
        <div className="native-store-switcher-bar">
          <span className="switcher-label">Store:</span>
          <div className="switcher-scroller">
            {sites.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`switcher-pill ${selectedSite?.id === s.id ? "active" : ""}`}
                onClick={() => setSelectedSiteId(s.id)}
              >
                {s.name || s.slug}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty State: No Store Launched Yet */}
      {sites.length === 0 && !loadingSites ? (
        <div className="native-dashboard-empty">
          <span style={{ fontSize: "44px" }}>🚀</span>
          <h2>Launch Your First Store</h2>
          <p>
            Choose from 100+ business website templates with pre-loaded demo products, 0% commission direct UPI, and instant WhatsApp alerts.
          </p>
          <button type="button" className="btn-native-primary" onClick={onBrowseStores}>
            Browse 100+ Stores &amp; Launch (7 Days Free)
          </button>
        </div>
      ) : selectedSite ? (
        <>
          {/* Active Store Hero Card */}
          <div className="native-active-store-card">
            <div className="store-card-top-row">
              <div className="store-identity">
                <span className="store-avatar-circle">
                  {(selectedSite.name || "S").charAt(0).toUpperCase()}
                </span>
                <div>
                  <h2 className="store-card-name">{selectedSite.name}</h2>
                  <a
                    href={`https://${selectedSite.slug}.ownezzy.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-card-link"
                  >
                    {selectedSite.slug}.ownezzy.com ↗
                  </a>
                </div>
              </div>
              <span className="store-plan-badge">
                {selectedSite.plan === "trial" ? "7-Day Free Trial" : "₹399/mo Active"}
              </span>
            </div>

            <div className="store-quick-links-row">
              <a
                href={`https://${selectedSite.slug}.ownezzy.com`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-store-action-link"
              >
                🌐 Visit Storefront
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `Check out our official online store: https://${selectedSite.slug}.ownezzy.com`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-store-action-link"
              >
                💬 Share on WhatsApp
              </a>
              <button
                type="button"
                className="btn-store-action-link"
                onClick={() => setSubView("settings")}
              >
                ⚙️ Settings
              </button>
            </div>
          </div>

          {/* 2x2 Real-Time KPI Grid */}
          <div className="native-kpi-grid">
            <div
              className={`kpi-card ${activePendingOrders.length > 0 ? "highlight-kpi" : ""}`}
              onClick={() => {
                setOrderFilter("pending");
                setSubView("orders");
              }}
              role="button"
              tabIndex={0}
            >
              <div className="kpi-top">
                <span className="kpi-label">Pending Action</span>
                <span className="kpi-icon">⏳</span>
              </div>
              <span className="kpi-value">{activePendingOrders.length}</span>
              <span className="kpi-sub">Tap to process</span>
            </div>

            <div
              className="kpi-card"
              onClick={() => {
                setOrderFilter("all");
                setSubView("orders");
              }}
              role="button"
              tabIndex={0}
            >
              <div className="kpi-top">
                <span className="kpi-label">Total Orders</span>
                <span className="kpi-icon">📦</span>
              </div>
              <span className="kpi-value">{orders.length}</span>
              <span className="kpi-sub">All-time count</span>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <span className="kpi-label">Total Sales</span>
                <span className="kpi-icon">💰</span>
              </div>
              <span className="kpi-value">{money(totalRevenue)}</span>
              <span className="kpi-sub">0% platform fee</span>
            </div>

            <div
              className="kpi-card"
              onClick={() => {
                setOrderFilter("delivered");
                setSubView("orders");
              }}
              role="button"
              tabIndex={0}
            >
              <div className="kpi-top">
                <span className="kpi-label">Delivered</span>
                <span className="kpi-icon">🎉</span>
              </div>
              <span className="kpi-value">{completedOrdersCount}</span>
              <span className="kpi-sub">Fulfilled orders</span>
            </div>
          </div>

          {/* Quick Action Tiles Grid */}
          <div className="native-quick-actions-section">
            <h3 className="section-title">Quick Actions</h3>
            <div className="native-action-tiles-grid">
              <button
                type="button"
                className="action-tile"
                onClick={() => {
                  setOrderFilter("all");
                  setSubView("orders");
                }}
              >
                <span className="tile-icon">📦</span>
                <span className="tile-label">Orders</span>
                {activePendingOrders.length > 0 && (
                  <span className="tile-badge">{activePendingOrders.length}</span>
                )}
              </button>

              <button
                type="button"
                className="action-tile"
                onClick={() => setSubView("settings")}
              >
                <span className="tile-icon">⚙️</span>
                <span className="tile-label">Store Settings</span>
              </button>

              <button
                type="button"
                className="action-tile"
                onClick={onBrowseStores}
              >
                <span className="tile-icon">🚀</span>
                <span className="tile-label">New Store</span>
              </button>

              <a
                href="/downloads/ownezzy.apk"
                download="ownezzy.apk"
                className="action-tile"
              >
                <span className="tile-icon">📱</span>
                <span className="tile-label">Mobile APK</span>
              </a>

              <a
                href="https://wa.me/918893725078?text=Hi%20Ownezzy%20Support,%20I%20need%20help%20with%20my%20store"
                target="_blank"
                rel="noopener noreferrer"
                className="action-tile"
              >
                <span className="tile-icon">💬</span>
                <span className="tile-label">WhatsApp Help</span>
              </a>

              <button
                type="button"
                className="action-tile"
                onClick={() => setSubView("fullAdmin")}
              >
                <span className="tile-icon">💻</span>
                <span className="tile-label">Workspace</span>
              </button>
            </div>
          </div>

          {/* Recent Orders Preview Card */}
          <div className="native-recent-orders-card">
            <div className="recent-orders-head">
              <h3>Recent Orders</h3>
              {orders.length > 0 && (
                <button
                  type="button"
                  className="btn-view-all-orders"
                  onClick={() => {
                    setOrderFilter("all");
                    setSubView("orders");
                  }}
                >
                  View All ({orders.length}) ›
                </button>
              )}
            </div>

            {loadingOrders ? (
              <div className="native-loading-box">
                <span className="native-spinner" />
                <span>Loading recent orders…</span>
              </div>
            ) : orders.length === 0 ? (
              <div className="native-no-orders-box">
                <span>🛍️</span>
                <p>No orders yet. Share your store link on WhatsApp to get your first order!</p>
              </div>
            ) : (
              <div className="recent-orders-list">
                {orders.slice(0, 4).map((order) => {
                  const whatsappUrl = getWhatsappUrl(order);
                  return (
                    <div key={order.id} className="recent-order-row">
                      <div className="ror-left">
                        <div className="ror-title-line">
                          <b>#{order.id}</b>
                          <span>•</span>
                          <span>{order.customer?.name || "Customer"}</span>
                          <span>•</span>
                          <span className={`ror-status ${order.status?.toLowerCase()}`}>
                            {order.status || "Pending"}
                          </span>
                        </div>
                        <span className="ror-amount">{money(order.total)}</span>
                      </div>

                      <div className="ror-actions">
                        {whatsappUrl && (
                          <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ror-wa"
                            title="Chat on WhatsApp"
                          >
                            💬
                          </a>
                        )}
                        {(order.status || "").toLowerCase() === "pending" && (
                          <button
                            type="button"
                            className="btn-ror-accept"
                            onClick={() => handleUpdateStatus(order.id, "Confirmed")}
                            title="Confirm Order"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
